import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { NotificationService, Notification, NotificationType } from '../services/NotificationService';
import { PlayFab } from '../services/PlayFab';
import { Account } from '../services/Account';

// WebSocket URL (same as Network.ts)
const SOCKET_URL = 'wss://8kh9tm8lfc.execute-api.eu-north-1.amazonaws.com/production/';

// Context types
interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  // Actions
  showToast: (notification: Notification) => void;
  markAsRead: (notificationId: string, timestamp: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  clearToast: (notificationId: string) => void;
  // Active toasts for display
  activeToasts: Notification[];
  // Dev/testing
  showMockNotification: (type?: NotificationType) => void;
}

interface NotificationProviderProps {
  children: React.ReactNode;
}

// Create context with default values
const NotificationContext = createContext<NotificationContextValue>({
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  showToast: () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  refreshNotifications: async () => {},
  clearToast: () => {},
  activeToasts: [],
  showMockNotification: () => {}
});

// Custom hook for using notifications
export const useNotifications = () => useContext(NotificationContext);

// Toast auto-dismiss duration (ms)
const TOAST_DURATION = 5000;
const MAX_TOASTS = 3;

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [activeToasts, setActiveToasts] = useState<Notification[]>([]);
  
  const socketRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toastTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Show a toast notification
  const showToast = useCallback((notification: Notification) => {
    setActiveToasts(prev => {
      // Limit number of visible toasts
      const newToasts = [notification, ...prev].slice(0, MAX_TOASTS);
      return newToasts;
    });

    // Auto-dismiss after duration
    const timeout = setTimeout(() => {
      clearToast(notification.id);
    }, TOAST_DURATION);
    
    toastTimeoutsRef.current.set(notification.id, timeout);
  }, []);

  // Clear a toast
  const clearToast = useCallback((notificationId: string) => {
    setActiveToasts(prev => prev.filter(t => t.id !== notificationId));
    
    const timeout = toastTimeoutsRef.current.get(notificationId);
    if (timeout) {
      clearTimeout(timeout);
      toastTimeoutsRef.current.delete(notificationId);
    }
  }, []);

  // Mark a notification as read
  const markAsRead = useCallback(async (notificationId: string, timestamp: number) => {
    const success = await NotificationService.markAsRead(notificationId, timestamp);
    if (success) {
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    const success = await NotificationService.markAllAsRead();
    if (success) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  }, []);

  // Refresh notifications from server
  const refreshNotifications = useCallback(async () => {
    if (!Account.isLoggedIn()) return;
    
    const [notifs, count] = await Promise.all([
      NotificationService.getNotifications(50),
      NotificationService.getUnreadCount()
    ]);
    
    setNotifications(notifs);
    setUnreadCount(count);
  }, []);

  // Generate a random ID
  const generateId = () => Math.random().toString(36).substring(2, 15);

  // Mock notification data for testing
  const MOCK_NOTIFICATIONS: Record<NotificationType, () => Notification> = {
    quest_completed: () => ({
      id: generateId(),
      type: 'quest_completed',
      title: 'Quest Complete!',
      message: 'Win 3 races - Completed!',
      icon: '✅',
      color: '#4CAF50',
      data: { QuestDescription: 'Win 3 races', QuestReward: 150 },
      read: false,
      createdAt: Date.now()
    }),
    battlepass_reward: () => ({
      id: generateId(),
      type: 'battlepass_reward',
      title: 'Battlepass Reward!',
      message: 'Level 5 reward is ready to claim!',
      icon: '🎁',
      color: '#9C27B0',
      data: { Level: 5, AssetId: 'galaxy_paint' },
      read: false,
      createdAt: Date.now()
    }),
    bingo_win: () => ({
      id: generateId(),
      type: 'bingo_win',
      title: 'BINGO!',
      message: 'You won with a diagonal!',
      icon: '🎉',
      color: '#FF9800',
      data: { WinType: 'diagonal' },
      read: false,
      createdAt: Date.now()
    }),
    trophy_unlocked: () => ({
      id: generateId(),
      type: 'trophy_unlocked',
      title: 'Trophy Unlocked!',
      message: 'Speed Demon - First place 10 times',
      icon: '🏆',
      color: '#FFD700',
      data: { TrophyName: 'Speed Demon' },
      read: false,
      createdAt: Date.now()
    }),
    item_received: () => ({
      id: generateId(),
      type: 'item_received',
      title: 'New Item!',
      message: 'Galaxy Paint added to your inventory',
      icon: '🎨',
      color: '#2196F3',
      data: { ItemName: 'Galaxy Paint', Source: 'battlepass' },
      read: false,
      createdAt: Date.now()
    }),
    milestone_completed: () => ({
      id: generateId(),
      type: 'milestone_completed',
      title: 'Milestone Reached!',
      message: 'Finish 100 races - Level 3 Complete!',
      icon: '🌟',
      color: '#E91E63',
      data: { MilestoneDescription: 'Finish 100 races', Level: 3 },
      read: false,
      createdAt: Date.now()
    })
  };

  // Show a mock notification for testing
  const showMockNotification = useCallback((type?: NotificationType) => {
    const types: NotificationType[] = ['quest_completed', 'battlepass_reward', 'bingo_win', 'trophy_unlocked', 'item_received', 'milestone_completed'];
    const selectedType = type || types[Math.floor(Math.random() * types.length)];
    const notification = MOCK_NOTIFICATIONS[selectedType]();
    
    // Add to notifications list
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);
    
    // Show toast
    showToast(notification);
  }, [showToast]);

  // Handle incoming WebSocket message
  const handleSocketMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      
      // Handle notification events
      if (data.id === 'notification' && data.notification) {
        const notification: Notification = data.notification;
        
        // Add to notifications list
        setNotifications(prev => [notification, ...prev]);
        
        // Update unread count
        if (!notification.read) {
          setUnreadCount(prev => prev + 1);
        }
        
        // Show toast
        showToast(notification);
      }
      
      // Handle registration confirmation
      if (data.id === 'notifications-registered') {
        console.log('[Notifications] WebSocket registration confirmed');
      }
    } catch (error) {
      // Not all messages are JSON or notification-related
    }
  }, [showToast]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const playFabId = PlayFab.getPlayerId();
    if (!playFabId) {
      console.log('[Notifications] Not logged in, skipping WebSocket connection');
      return;
    }

    // Don't reconnect if already connected
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    console.log('[Notifications] Connecting to WebSocket...');
    
    const socket = new WebSocket(SOCKET_URL);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log('[Notifications] WebSocket connected');
      setIsConnected(true);
      
      // Register for notifications
      socket.send(JSON.stringify({
        id: 'register-notifications',
        playFabId: playFabId,
        username: Account.getUserName() || playFabId
      }));

      // Start ping interval
      pingIntervalRef.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ id: 'ping' }));
        }
      }, 30000);
    };

    socket.onmessage = handleSocketMessage;

    socket.onclose = (event) => {
      console.log('[Notifications] WebSocket disconnected', event.code);
      setIsConnected(false);
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Attempt reconnect if still logged in
      if (Account.isLoggedIn()) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[Notifications] Attempting reconnect...');
          connectWebSocket();
        }, 3000);
      }
    };

    socket.onerror = (error) => {
      console.error('[Notifications] WebSocket error:', error);
    };
  }, [handleSocketMessage]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      // Unregister before closing
      const playFabId = PlayFab.getPlayerId();
      if (playFabId && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          id: 'unregister-notifications',
          playFabId
        }));
      }
      
      socketRef.current.close();
      socketRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  // Effect: Connect when user logs in, disconnect when logs out
  useEffect(() => {
    // Check initial login state
    if (Account.isLoggedIn()) {
      connectWebSocket();
      refreshNotifications();
    }

    // Listen for login/logout events
    const handleLogin = () => {
      connectWebSocket();
      refreshNotifications();
    };

    const handleLogout = () => {
      disconnectWebSocket();
      setNotifications([]);
      setUnreadCount(0);
      setActiveToasts([]);
    };

    window.addEventListener('userLoggedIn', handleLogin);
    window.addEventListener('userLoggedOut', handleLogout);

    return () => {
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('userLoggedOut', handleLogout);
      disconnectWebSocket();
      
      // Clear all toast timeouts
      toastTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      toastTimeoutsRef.current.clear();
    };
  }, [connectWebSocket, disconnectWebSocket, refreshNotifications]);

  // Context value
  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    isConnected,
    showToast,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    clearToast,
    activeToasts,
    showMockNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;

